/**
 * Modal d'édition d'un groupe
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useGroups } from "@core/hooks/useGroups";
import { X, Save, Mail, Calendar, Clock } from "lucide-react";
import { useToast } from "@core/hooks/use-toast";

interface GroupEditorProps {
  groupId: string;
  onClose: () => void;
}

export function GroupEditor({ groupId, onClose }: GroupEditorProps) {
  const { t } = useTranslation("polls");
  const { getGroup, updateGroup } = useGroups();
  const { toast } = useToast();
  const group = getGroup(groupId);

  const [name, setName] = useState(group?.name || "");
  const [emailsText, setEmailsText] = useState(
    group?.member_emails.join(", ") || "",
  );
  const [saving, setSaving] = useState(false);

  // Fonction de validation d'email avec regex
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Fonction pour valider tous les emails et retourner les erreurs
  const validateEmails = (
    emails: string[],
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    emails.forEach((email, index) => {
      if (!validateEmail(email)) {
        if (email.length === 0) {
          errors.push(t("groups.email_error_empty", { index: index + 1 }));
        } else if (!email.includes("@")) {
          errors.push(t("groups.email_error_missing_at", { email }));
        } else if (!email.includes(".")) {
          errors.push(t("groups.email_error_missing_domain", { email }));
        } else if (email.startsWith("@")) {
          errors.push(t("groups.email_error_missing_name", { email }));
        } else if (email.endsWith("@")) {
          errors.push(
            t("groups.email_error_missing_domain_after_at", { email }),
          );
        } else if (email.includes("@@")) {
          errors.push(t("groups.email_error_double_at", { email }));
        } else {
          errors.push(t("groups.email_error_invalid_format", { email }));
        }
      }
    });

    return { isValid: errors.length === 0, errors };
  };

  // États pour les digests hebdomadaires
  const [weeklyDigestEnabled, setWeeklyDigestEnabled] = useState(
    group?.weekly_digest_enabled || false,
  );
  const [digestDay, setDigestDay] = useState(group?.digest_day || 1); // 1=Lundi
  const [digestInterval, setDigestInterval] = useState(
    group?.digest_interval || 1,
  ); // 1=chaque semaine

  useEffect(() => {
    if (group) {
      setName(group.name);
      setEmailsText(group.member_emails.join(", "));
      setWeeklyDigestEnabled(group.weekly_digest_enabled || false);
      setDigestDay(group.digest_day || 1);
      setDigestInterval(group.digest_interval || 1);
    }
  }, [group]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        title: t("groups.name_required_title"),
        description: t("groups.name_required_desc"),
        variant: "destructive",
      });
      return;
    }

    const emails = emailsText
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);

    if (emails.length === 0) {
      toast({
        title: t("groups.emails_required_title"),
        description: t("groups.emails_required_desc"),
        variant: "destructive",
      });
      return;
    }

    // Validation des formats d'email
    const emailValidation = validateEmails(emails);
    if (!emailValidation.isValid) {
      const errorMessage =
        emailValidation.errors.length > 3
          ? t("groups.emails_invalid_count", {
              count: emailValidation.errors.length,
            })
          : t("groups.emails_invalid_list", {
              errors: emailValidation.errors.join(", "),
            });

      toast({
        title: t("groups.email_format_invalid_title"),
        description: errorMessage,
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    const updated = await updateGroup(groupId, {
      name: name.trim(),
      member_emails: emails,
      weekly_digest_enabled: weeklyDigestEnabled,
      digest_day: weeklyDigestEnabled ? digestDay : null,
      digest_interval: weeklyDigestEnabled ? digestInterval : null,
    });
    setSaving(false);

    if (updated) {
      toast({
        title: t("groups.updated_title"),
        description: t("groups.updated_desc", {
          name: updated.name,
          count: updated.member_emails.length,
        }),
      });
      onClose();
    } else {
      toast({
        title: t("groups.update_error_title"),
        description: t("groups.update_error_desc"),
        variant: "destructive",
      });
    }
  };

  if (!group) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-testid="group-editor-modal"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {t("groups.edit_title")}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            data-testid="close-modal-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("groups.name_label")}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("groups.name_placeholder")}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              data-testid="group-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("groups.emails_label")}
            </label>
            <textarea
              value={emailsText}
              onChange={(e) => setEmailsText(e.target.value)}
              placeholder="alice@example.com, bob@example.com, charlie@example.com"
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
              data-testid="group-email-input"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t("groups.member_count", {
                count: emailsText
                  .split(",")
                  .map((e) => e.trim())
                  .filter(Boolean).length,
              })}
            </p>
          </div>
        </div>

        {/* Section Digest Hebdomadaire */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {t("groups.weekly_email_title")}
            </h3>
          </div>

          <div className="space-y-4">
            {/* Checkbox pour activer/désactiver */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="weekly-digest-enabled"
                checked={weeklyDigestEnabled}
                onChange={(e) => setWeeklyDigestEnabled(e.target.checked)}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <label
                htmlFor="weekly-digest-enabled"
                className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                {t("groups.weekly_digest_enable_label")}
              </label>
            </div>

            {weeklyDigestEnabled && (
              <div className="ml-7 space-y-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">
                  {t("groups.weekly_digest_intro")}
                </p>

                {/* Sélection du jour */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    {t("groups.send_day_label")}
                  </label>
                  <select
                    value={digestDay}
                    onChange={(e) => setDigestDay(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value={1}>{t("groups.day_monday")}</option>
                    <option value={2}>{t("groups.day_tuesday")}</option>
                    <option value={3}>{t("groups.day_wednesday")}</option>
                    <option value={4}>{t("groups.day_thursday")}</option>
                    <option value={5}>{t("groups.day_friday")}</option>
                    <option value={6}>{t("groups.day_saturday")}</option>
                    <option value={7}>{t("groups.day_sunday")}</option>
                  </select>
                </div>

                {/* Sélection de l'intervalle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    {t("groups.send_frequency_label")}
                  </label>
                  <select
                    value={digestInterval}
                    onChange={(e) => setDigestInterval(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value={1}>{t("groups.freq_every_week")}</option>
                    <option value={2}>
                      {t("groups.freq_every_n_weeks", { count: 2 })}
                    </option>
                    <option value={3}>
                      {t("groups.freq_every_n_weeks", { count: 3 })}
                    </option>
                    <option value={4}>
                      {t("groups.freq_every_n_weeks", { count: 4 })}
                    </option>
                    <option value={6}>
                      {t("groups.freq_every_n_weeks", { count: 6 })}
                    </option>
                    <option value={8}>
                      {t("groups.freq_every_n_weeks", { count: 8 })}
                    </option>
                    <option value={12}>
                      {t("groups.freq_every_3_months")}
                    </option>
                    <option value={24}>
                      {t("groups.freq_every_6_months")}
                    </option>
                    <option value={52}>{t("groups.freq_every_year")}</option>
                  </select>
                </div>

                {/* Résumé de la configuration */}
                <div className="text-xs text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30 p-3 rounded-md">
                  {t("groups.config_summary", {
                    frequency:
                      digestInterval === 1
                        ? t("groups.config_every_week")
                        : t("groups.config_every_n_weeks", {
                            count: digestInterval,
                          }),
                    day: [
                      t("groups.day_sunday"),
                      t("groups.day_monday"),
                      t("groups.day_tuesday"),
                      t("groups.day_wednesday"),
                      t("groups.day_thursday"),
                      t("groups.day_friday"),
                      t("groups.day_saturday"),
                    ][digestDay],
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="cancel-group-button"
          >
            {t("groups.cancel")}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            data-testid="save-group-button"
          >
            <Save className="w-4 h-4" />
            {saving ? t("groups.saving") : t("groups.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
